import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import i18n from '../../../i18n';
import { joinApiBaseUrl } from '../functions/resolveRestUrl';
import useForm from '../use-form';
import { STAGED_UPLOAD_PREFIX } from '../functions/stagedUpload';

function isHttpUrl(value) {
  return typeof value === 'string' && (value.startsWith('http://') || value.startsWith('https://'));
}

function parseUploadResponse(data) {
  const ids = Array.isArray(data?.ids)
    ? data.ids
    : data?.id
      ? [data.id]
      : data?.upload_ref
        ? [`${STAGED_UPLOAD_PREFIX}${data.upload_ref}`]
        : [];

  const urls = Array.isArray(data?.urls)
    ? data.urls
    : typeof data?.url === 'string'
      ? [data.url]
      : [];

  if (ids.length === 0) {
    throw new Error('Upload response missing ids');
  }

  return { ids, urls };
}

const AttachmentInputBase = ({
  fieldName,
  label,
  value = null,
  errors = [],
  helpText = '',
  style = {},
  onChange = () => {},
  many = false,
  accept,
  maxSize = 10485760,
  minSize = 0,
  previewType = 'file',
  baseUrl,
  resourceFieldName,
  singleItemLabel = 'file',
  multipleItemLabel = 'files'
}) => {
  const objectUrlsRef = useRef([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [uploadedUrls, setUploadedUrls] = useState(() => (many ? [] : null));
  const handleFormResponse = useCallback(() => {}, []);
  const { sendRequest } = useForm(handleFormResponse);

  const revokeAllObjectUrls = useCallback(() => {
    objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    objectUrlsRef.current = [];
  }, []);

  const registerObjectUrl = useCallback((file) => {
    const blobUrl = URL.createObjectURL(file);
    objectUrlsRef.current.push(blobUrl);
    return blobUrl;
  }, []);

  const updateProgress = useCallback((completed, total) => {
    setProgress(total > 0 ? completed / total : 0);
  }, []);

  useEffect(() => () => revokeAllObjectUrls(), [revokeAllObjectUrls]);

  const uploadAttachments = useCallback(
    async (files) => {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append(resourceFieldName, file, file.name || 'upload');
      });
      const response = await sendRequest({
        url: joinApiBaseUrl(`${baseUrl}add/`),
        method: 'POST',
        data: formData,
        json: true
      });

      return parseUploadResponse(response);
    },
    [baseUrl, resourceFieldName, sendRequest]
  );

  const onDrop = useCallback(
    async (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        const first = rejectedFiles[0];
        const errorMessage =
          first.errors?.[0]?.message || i18n.t('Some files were rejected. Check type and size.');
        setUploadError(errorMessage);
        return;
      }

      if (!acceptedFiles.length) {
        return;
      }

      setUploadError(null);
      setUploading(true);
      setProgress(0);
      let nextValue = value;
      onChange({
        name: fieldName,
        value: nextValue,
        label,
        uploading: true
      });

      try {
        if (many) {
          const existingValues = Array.isArray(value) ? [...value] : [];
          const startIndex = existingValues.length;
          const uploadData = await uploadAttachments(acceptedFiles);
          const uploadedIds = uploadData.ids;
          const nextUrls = uploadData.urls;

          if (uploadedIds.length > 0) {
            updateProgress(1, 1);

            setUploadedUrls((previous) => {
              const next = Array.isArray(previous) ? [...previous] : [];
              while (next.length < startIndex) {
                next.push(null);
              }
              nextUrls.forEach((url, index) => {
                next[startIndex + index] = url || null;
              });
              return next;
            });

            onChange({
              name: fieldName,
              value: [...existingValues, ...uploadedIds],
              label
            });
            nextValue = [...existingValues, ...uploadedIds];
          }
        } else {
          const file = acceptedFiles[0];
          const uploadData = await uploadAttachments([file]);
          const id = uploadData.ids[0];

          revokeAllObjectUrls();
          setUploadedUrls(uploadData.urls[0] || null);
          updateProgress(1, 1);

          onChange({
            name: fieldName,
            value: id,
            label
          });
          nextValue = id;
        }
      } catch (error) {
        setUploadError(error.message || i18n.t('Upload failed'));
      } finally {
        setUploading(false);
        setProgress(0);
        onChange({
          name: fieldName,
          value: nextValue,
          label,
          uploading: false
        });
      }
    },
    [fieldName, label, many, onChange, revokeAllObjectUrls, updateProgress, uploadAttachments, value]
  );

  const removeAt = useCallback(
    (index) => {
      if (many) {
        const nextValue = Array.isArray(value) ? [...value] : [];
        nextValue.splice(index, 1);

        setUploadedUrls((previous) => {
          const next = Array.isArray(previous) ? [...previous] : [];
          next.splice(index, 1);
          return next;
        });

        onChange({
          name: fieldName,
          value: nextValue.length ? nextValue : null,
          label
        });
        return;
      }

      revokeAllObjectUrls();
      setUploadedUrls(null);
      onChange({
        name: fieldName,
        value: null,
        label
      });
    },
    [fieldName, label, many, onChange, revokeAllObjectUrls, value]
  );

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject
  } = useDropzone({
    onDrop,
    accept,
    maxSize,
    minSize,
    maxFiles: many ? undefined : 1,
    multiple: many,
    disabled: uploading
  });

  const dropzoneClasses = useMemo(() => {
    let classes =
      'dropzone-container border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200';

    if (uploading) {
      classes += ' opacity-75 pointer-events-none';
    } else {
      classes += ' cursor-pointer';
    }

    if (isDragActive) {
      classes += ' border-primary bg-primary-light';
    } else if (isDragAccept) {
      classes += ' border-success bg-success-light';
    } else if (isDragReject) {
      classes += ' border-danger bg-danger-light';
    } else {
      classes += ' border-gray-300 hover:border-primary hover:bg-gray-50';
    }

    if (errors.length > 0) {
      classes += ' border-danger';
    }

    return classes;
  }, [errors.length, isDragActive, isDragAccept, isDragReject, uploading]);

  const shouldRenderImagePreview = previewType === 'image';
  const dropLabel = many ? i18n.t(multipleItemLabel) : i18n.t(`a single ${singleItemLabel}`);

  const renderPreview = useCallback(
    (previewUrl, index, key, buttonLabel) => {
      if (!previewUrl) {
        return null;
      }

      return (
        <div key={key} className={`d-flex ${shouldRenderImagePreview ? 'align-items-start' : 'align-items-center'} gap-2 flex-wrap`}>
          {shouldRenderImagePreview ? (
            <img
              src={previewUrl}
              alt=""
              className="rounded border"
              style={{ maxHeight: many ? '120px' : '140px', maxWidth: '100%', objectFit: 'contain' }}
            />
          ) : (
            <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="text-primary">
              {buttonLabel}
            </a>
          )}
          <button
            type="button"
            className="btn btn-link text-danger btn-sm p-0 align-self-start"
            onClick={() => removeAt(index)}
            title={i18n.t('Remove file')}
          >
            ✕
          </button>
        </div>
      );
    },
    [many, removeAt, shouldRenderImagePreview]
  );

  const renderPreviewBelow = () => {
    if (value == null || value === '') {
      return null;
    }

    if (many && Array.isArray(value)) {
      return (
        <div className="mt-3 d-flex flex-wrap gap-1 file-input-previews">
          {value.map((id, index) => {
            const uploadedUrl = Array.isArray(uploadedUrls) ? uploadedUrls[index] : null;

            if (uploadedUrl) {
              return renderPreview(uploadedUrl, index, `${id}-${index}-uploaded`, i18n.t('Download file'));
            }

            if (isHttpUrl(id)) {
              return renderPreview(id, index, `${id}-${index}-url`, i18n.t('Open file'));
            }

            return null;
          })}
        </div>
      );
    }

    const singleUploadedUrl = typeof uploadedUrls === 'string' ? uploadedUrls : null;

    if (singleUploadedUrl) {
      return (
        <div className="mt-3 file-input-previews d-flex align-items-start gap-2 flex-wrap">
          {renderPreview(singleUploadedUrl, 0, 'single-uploaded', i18n.t('Download file'))}
        </div>
      );
    }

    if (isHttpUrl(value)) {
      return (
        <div className="mt-3 file-input-previews d-flex align-items-start gap-2 flex-wrap">
          {renderPreview(value, 0, 'single-url', i18n.t('Download'))}
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <div
        {...getRootProps()}
        className={dropzoneClasses}
        style={{ ...(style || {}), cursor: uploading ? 'wait' : 'pointer' }}
      >
        <input {...getInputProps()} />
        <div
          className="dropzone-content py-3 flex-grow-1 d-flex align-items-center justify-content-center"
          style={{ border: '1px dashed var(--bs-border-color)' }}
        >
          {isDragActive ? (
            <div className="text-primary font-medium">
              {isDragAccept ? i18n.t('Drop files here...') : i18n.t('File type not accepted')}
            </div>
          ) : uploading ? (
            <div className="text-primary font-medium">{i18n.t('Uploading...')}</div>
          ) : (
            <>
              <div className="text-lg font-medium">
                {previewType === 'image' ? '🖼️ ' : '📁 '}
                {i18n.t('Drop ') + dropLabel + ' ' + i18n.t('here or click to select')}
              </div>
              <div className="text-xs text-gray-400" />
            </>
          )}
        </div>
      </div>

      {renderPreviewBelow()}

      {uploading && (
        <div
          className="progress mt-2"
          style={{ height: '8px' }}
          role="progressbar"
          aria-valuenow={Math.round(progress * 100)}
          aria-valuemin="0"
          aria-valuemax="100"
        >
          <div
            className="progress-bar progress-bar-striped progress-bar-animated"
            style={{ width: `${Math.max(5, progress * 100)}%` }}
          />
        </div>
      )}

      {uploadError && (
        <div className="text-danger small mt-2" role="alert">
          {uploadError}
        </div>
      )}

      {helpText && <div className="form-text mt-2 text-sm text-gray-600">{helpText}</div>}
    </>
  );
};

export default AttachmentInputBase;
